import React from 'react';
import {
	Box,
	makeStyles,
	TextField,
	Theme,
	Typography,
} from '@material-ui/core';
import { useAuth, useCurrentUser } from '@common/hooks';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@common/components';
import { registerError } from '@common/errors';

const useStyles = makeStyles((theme: Theme) => ({
	form: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: theme.spacing(1.5),
		marginTop: theme.spacing(2),
		width: '100%',

		[theme.breakpoints.up('sm')]: {
			flexDirection: 'row',
		},
	},
	inputs: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5),
		alignItems: 'center',

		[theme.breakpoints.up('sm')]: {
			width: '50%',
		},
	},
	actions: {
		[theme.breakpoints.up('sm')]: {
			alignSelf: 'flex-end',
		},
	},
	button: {
		[theme.breakpoints.up('sm')]: {
			width: '170px',
		},
	},
}));

export const UpdateEmail = (): React.ReactElement => {
	const styles = useStyles();
	const user = useCurrentUser();
	const { updateEmail, reauthenticate, currentUser: authUser } = useAuth();
	const [isPending, setIsPending] = React.useState(false);

	const onSubmit = React.useCallback(
		async ({ email, password }: { email: string; password: string }) => {
			setIsPending(true);
			try {
				await reauthenticate(user.email, password);
				await updateEmail(email, window.location.href);
			} catch (err) {
				registerError(err);
			} finally {
				setIsPending(false);
			}
		},
		[reauthenticate, updateEmail, user.email],
	);

	return (
		<Box>
			<Typography variant="h5" gutterBottom>
				Update Email
			</Typography>

			<Formik
				enableReinitialize
				initialValues={{
					email: authUser?.email || '',
					password: '',
				}}
				onSubmit={onSubmit}
				submitting={false}
				validateOnMount={false}
				validationSchema={Yup.object().shape({
					email: Yup.string()
						.email('Email is not valid')
						.required('Email is a required field'),
					password: Yup.string().required(
						'Password is a required field',
					),
				})}
			>
				{({
					dirty,
					isValid,
					values,
					errors,
					handleChange,
					handleBlur,
					handleSubmit,
				}) => (
					<Form
						onSubmit={(event) => {
							event.preventDefault();
							handleSubmit();
						}}
					>
						<div className={styles.form}>
							<div className={styles.inputs}>
								<TextField
									type="email"
									fullWidth
									variant="outlined"
									aria-label="Email"
									placeholder="myname@myemail.com"
									name="email"
									value={values.email}
									onChange={handleChange}
									onBlur={handleBlur}
									helperText={errors.email}
									error={Boolean(errors.email)}
								/>
								<TextField
									type="password"
									fullWidth
									variant="outlined"
									aria-label="Password"
									placeholder="Your password"
									name="password"
									value={values.password}
									onChange={handleChange}
									onBlur={handleBlur}
									helperText={errors.password}
									error={Boolean(errors.password)}
								/>
							</div>
							<div className={styles.actions}>
								<LoadingButton
									className={styles.button}
									type="submit"
									loading={isPending}
									variant="contained"
									color="primary"
									fullWidth
									disabled={!dirty || !isValid}
								>
									Save
								</LoadingButton>
							</div>
						</div>
					</Form>
				)}
			</Formik>
		</Box>
	);
};
